import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Crown, Users, Shield, Trophy, Bot, ScrollText, Calendar, Megaphone,
  RefreshCw, BarChart3, Trash2, FileText, HardDrive,
} from 'lucide-react';

// Sub-components
import { useSuperAdminData } from './superadmin/useSuperAdminData';
import { MarketControl } from './superadmin/MarketControl';
import { UsersTab } from './superadmin/UsersTab';
import { ClubsTab } from './superadmin/ClubsTab';
import { ChampionshipsTab } from './superadmin/ChampionshipsTab';
import { MatchesTab } from './superadmin/MatchesTab';
import { BotTab } from './superadmin/BotTab';
import { LogsTab } from './superadmin/LogsTab';
import { BannersTab } from './superadmin/BannersTab';
import { StatsTab } from './superadmin/StatsTab';
import { TrashTab } from './superadmin/TrashTab';
import { ReportsTab } from './superadmin/ReportsTab';
import { FileManagerTab } from './superadmin/FileManagerTab';
import {
  AddPhraseDialog, BannerDialog, CoinsDialog,
  DeleteClubDialog, DeletePlayerDialog, DeleteUserDialog,
  BulkDeleteDialog, CreateChampionshipDialog, DeleteChampionshipDialog,
  BanUserDialog, SelfBanDialog, EditClubDialog, SendCoinsDialog,
  RosterDialog, CreateClubDialog, ChangePasswordDialog, CreateUserDialog,
  AddMemberDialog,
} from './superadmin/SuperAdminDialogs';

const SuperAdmin = () => {
  const d = useSuperAdminData();

  const adminTabs = [
    { id: 'stats', label: d.isItalian ? 'Statistiche' : 'Stats', icon: BarChart3 },
    { id: 'users', label: d.isItalian ? 'Utenti' : 'Users', icon: Users },
    { id: 'clubs', label: 'Club', icon: Shield },
    { id: 'championships', label: d.isItalian ? 'Campionati' : 'Champ.', icon: Trophy },
    { id: 'matches', label: d.isItalian ? 'Partite' : 'Matches', icon: Calendar },
    { id: 'reports', label: 'Report', icon: FileText },
    { id: 'bot', label: 'Bot', icon: Bot, onClick: () => d.fetchBotPhrases() },
    { id: 'logs', label: 'Log', icon: ScrollText, onClick: () => d.fetchActivityLogs() },
    { id: 'banners', label: 'Banner', icon: Megaphone, onClick: () => d.fetchBanners() },
    { id: 'trash', label: d.isItalian ? 'Cestino' : 'Trash', icon: Trash2 },
    { id: 'files', label: 'File', icon: HardDrive },
  ];

  if (d.loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#FFEA00] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded bg-[#FFEA00]/20 flex items-center justify-center flex-shrink-0">
            <Crown size={20} className="text-[#FFEA00] md:w-6 md:h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white truncate">Super Admin</h1>
            <p className="text-slate-400 text-sm md:text-base">{d.isItalian ? 'Pannello di controllo completo' : 'Full control panel'}</p>
          </div>
        </div>
        <Button onClick={d.fetchData} variant="outline" className="border-white/10 w-full md:w-auto">
          <RefreshCw size={16} className="mr-2" />{d.isItalian ? 'Aggiorna' : 'Refresh'}
        </Button>
      </motion.div>

      {/* Stats Grid */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {[
          { icon: Users, color: 'text-[#00F0FF]', value: d.stats.totalUsers, label: d.isItalian ? 'Utenti Totali' : 'Total Users' },
          { icon: Shield, color: 'text-[#00E676]', value: d.stats.totalClubs, label: 'Club' },
          { icon: Trophy, color: 'text-[#FFEA00]', value: d.stats.activeChampionships, label: d.isItalian ? 'Campionati' : 'Championships' },
          { icon: Crown, color: 'text-[#D500F9]', value: d.stats.superAdmins + d.stats.admins, label: d.isItalian ? 'Amministratori' : 'Admins' },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-[#0a0d15] border border-white/10 p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <Icon size={18} className={`${stat.color} flex-shrink-0`} />
                <div className="min-w-0">
                  <p className="text-xl md:text-2xl font-bold font-mono text-white">{stat.value}</p>
                  <p className="text-[10px] md:text-xs text-slate-500 uppercase truncate">{stat.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </motion.div>

      {/* Market Control */}
      <MarketControl
        isItalian={d.isItalian}
        marketStatus={d.marketStatus}
        togglingMarket={d.togglingMarket}
        handleToggleMarket={d.handleToggleMarket}
        showScheduleModal={d.showScheduleModal}
        setShowScheduleModal={d.setShowScheduleModal}
        scheduleOpenAt={d.scheduleOpenAt}
        setScheduleOpenAt={d.setScheduleOpenAt}
        scheduleCloseAt={d.scheduleCloseAt}
        setScheduleCloseAt={d.setScheduleCloseAt}
        savingSchedule={d.savingSchedule}
        handleSaveSchedule={d.handleSaveSchedule}
      />

      {/* Main Content */}
      <div className="space-y-4 md:space-y-6">
        {/* Tab Bar */}
        <div className="overflow-x-auto -mx-4 px-4">
          <div className="bg-[#0a0d15] border border-white/10 p-1 inline-flex min-w-max rounded-lg" role="tablist">
            {adminTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  onClick={() => { d.setActiveTab(tab.id); tab.onClick?.(); }}
                  className={`inline-flex items-center px-2 md:px-3 py-1.5 rounded-md uppercase tracking-wider text-[10px] md:text-xs font-bold transition-colors ${
                    d.activeTab === tab.id ? 'bg-[#FFEA00] text-black' : 'text-slate-400 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon size={12} className="mr-1 md:mr-2" />{tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        {d.activeTab === 'stats' && <StatsTab isItalian={d.isItalian} championships={d.championships} />}

        {d.activeTab === 'users' && (
          <UsersTab
            isItalian={d.isItalian} currentUserId={d.user.user_id}
            filteredUsers={d.filteredUsers} searchQuery={d.searchQuery} setSearchQuery={d.setSearchQuery}
            selectedUserIds={d.selectedUserIds} toggleSelectAll={d.toggleSelectAll} toggleUserSelection={d.toggleUserSelection}
            allSelectableSelected={d.filteredUsers.filter(u => u.user_id !== d.user.user_id).length > 0 && d.selectedUserIds.size === d.filteredUsers.filter(u => u.user_id !== d.user.user_id).length}
            handleUpdateRole={d.handleUpdateRole} handleToggleBan={d.handleToggleBan}
            handleOpenSendCoins={d.handleOpenSendCoins} handleOpenChangePassword={d.handleOpenChangePassword}
            handleOpenModifyCoins={d.handleOpenModifyCoins}
            handleDeleteUser={d.handleDeleteUser} setShowBulkDeleteDialog={d.setShowBulkDeleteDialog}
            setShowCreateUserDialog={d.setShowCreateUserDialog}
            handleReleasePlayer={d.handleReleasePlayer} clubs={d.clubs}
            getRoleBadge={d.getRoleBadge} usersTotal={d.usersTotal} usersPage={d.usersPage}
            usersPerPage={d.USERS_PER_PAGE} onPageChange={d.onPageChange}
          />
        )}

        {d.activeTab === 'clubs' && (
          <ClubsTab
            isItalian={d.isItalian} clubs={d.clubs} users={d.users}
            clubSearchQuery={d.clubSearchQuery} setClubSearchQuery={d.setClubSearchQuery}
            handleOpenCreateClub={d.handleOpenCreateClub} handleViewRoster={d.handleViewRoster}
            handleEditClub={d.handleEditClub} handleDeleteClub={d.handleDeleteClub}
            handleOpenAddMember={d.handleOpenAddMember}
          />
        )}

        {d.activeTab === 'championships' && (
          <ChampionshipsTab
            isItalian={d.isItalian} championships={d.championships} clubs={d.clubs}
            expandedChampClubs={d.expandedChampClubs} setExpandedChampClubs={d.setExpandedChampClubs}
            removingClub={d.removingClub}
            setShowCreateChampionshipDialog={d.setShowCreateChampionshipDialog}
            handleGenerateSchedule={d.handleGenerateSchedule} handleToggleRegistration={d.handleToggleRegistration}
            handleSuspendChampionship={d.handleSuspendChampionship} handleCompleteChampionship={d.handleCompleteChampionship}
            handleDeleteChampionship={d.handleDeleteChampionship} handleRemoveClubFromChampionship={d.handleRemoveClubFromChampionship}
            refreshData={d.fetchData}
          />
        )}

        {d.activeTab === 'matches' && (
          <MatchesTab
            isItalian={d.isItalian} championships={d.championships}
            matches={d.matches} matchesTotal={d.matchesTotal}
            selectedChampForMatches={d.selectedChampForMatches} setSelectedChampForMatches={d.setSelectedChampForMatches}
            fetchMatchesForChampionship={d.fetchMatchesForChampionship} setMatches={d.setMatches}
            loadingMatches={d.loadingMatches} loadingMoreMatches={d.loadingMoreMatches}
            editingMatch={d.editingMatch} setEditingMatch={d.setEditingMatch}
            matchDateInput={d.matchDateInput} setMatchDateInput={d.setMatchDateInput}
            handleUpdateMatchDate={d.handleUpdateMatchDate} handleCancelMatchReport={d.handleCancelMatchReport}
          />
        )}

        {d.activeTab === 'bot' && (
          <BotTab
            isItalian={d.isItalian} botPhrases={d.botPhrases} loadingPhrases={d.loadingPhrases}
            setShowAddPhraseDialog={d.setShowAddPhraseDialog}
            handleDeletePhrase={d.handleDeletePhrase} handleSeedBotPhrases={d.handleSeedBotPhrases}
          />
        )}

        {d.activeTab === 'logs' && (
          <LogsTab
            isItalian={d.isItalian} activityLogs={d.activityLogs} loadingLogs={d.loadingLogs}
            logFilter={d.logFilter} setLogFilter={d.setLogFilter} actionTypes={d.actionTypes}
            fetchActivityLogs={d.fetchActivityLogs} handleClearOldLogs={d.handleClearOldLogs}
            handleClearAllLogs={d.handleClearAllLogs} getActionLabel={d.getActionLabel} formatLogDate={d.formatLogDate}
          />
        )}

        {d.activeTab === 'banners' && (
          <BannersTab
            isItalian={d.isItalian} banners={d.banners} loadingBanners={d.loadingBanners}
            handleOpenBannerDialog={d.handleOpenBannerDialog} handleToggleBanner={d.handleToggleBanner}
            handleDeleteBanner={d.handleDeleteBanner} getBannerTypeLabel={d.getBannerTypeLabel}
          />
        )}

        {d.activeTab === 'trash' && (
          <TrashTab isItalian={d.isItalian} />
        )}

        {d.activeTab === 'reports' && (
          <ReportsTab isItalian={d.isItalian} />
        )}

        {d.activeTab === 'files' && (
          <FileManagerTab isItalian={d.isItalian} />
        )}
      </div>

      {/* All Dialogs */}
      <AddPhraseDialog isItalian={d.isItalian} open={d.showAddPhraseDialog} onOpenChange={d.setShowAddPhraseDialog} newPhrase={d.newPhrase} setNewPhrase={d.setNewPhrase} handleAddPhrase={d.handleAddPhrase} />
      <BannerDialog isItalian={d.isItalian} open={d.showBannerDialog} onOpenChange={d.setShowBannerDialog} editingBanner={d.editingBanner} bannerForm={d.bannerForm} setBannerForm={d.setBannerForm} handleSaveBanner={d.handleSaveBanner} />
      <CoinsDialog isItalian={d.isItalian} open={d.showCoinsDialog} onOpenChange={d.setShowCoinsDialog} selectedUser={d.selectedUser} clubs={d.clubs} coinsTarget={d.coinsTarget} setCoinsTarget={d.setCoinsTarget} coinsAction={d.coinsAction} setCoinsAction={d.setCoinsAction} coinsAmount={d.coinsAmount} setCoinsAmount={d.setCoinsAmount} handleModifyCoins={d.handleModifyCoins} />
      <DeleteClubDialog isItalian={d.isItalian} open={d.showDeleteDialog} onOpenChange={d.setShowDeleteDialog} clubToDelete={d.clubToDelete} confirmDeleteClub={d.confirmDeleteClub} deleting={d.deleting} />
      <DeletePlayerDialog isItalian={d.isItalian} open={d.showDeletePlayerDialog} onOpenChange={d.setShowDeletePlayerDialog} playerToDelete={d.playerToDelete} confirmDeletePlayer={d.confirmDeletePlayer} deleting={d.deleting} />
      <DeleteUserDialog isItalian={d.isItalian} open={d.showDeleteUserDialog} onOpenChange={d.setShowDeleteUserDialog} userToDelete={d.userToDelete} confirmDeleteUser={d.confirmDeleteUser} deleting={d.deleting} />
      <BulkDeleteDialog isItalian={d.isItalian} open={d.showBulkDeleteDialog} onOpenChange={d.setShowBulkDeleteDialog} selectedUserIds={d.selectedUserIds} users={d.users} confirmBulkDeleteUsers={d.confirmBulkDeleteUsers} bulkDeleting={d.bulkDeleting} />
      <CreateChampionshipDialog isItalian={d.isItalian} open={d.showCreateChampionshipDialog} onOpenChange={d.setShowCreateChampionshipDialog} championshipForm={d.championshipForm} setChampionshipForm={d.setChampionshipForm} creatingChampionship={d.creatingChampionship} handleCreateChampionship={d.handleCreateChampionship} />
      <DeleteChampionshipDialog isItalian={d.isItalian} open={d.showDeleteChampionshipDialog} onOpenChange={d.setShowDeleteChampionshipDialog} championshipToDelete={d.championshipToDelete} confirmDeleteChampionship={d.confirmDeleteChampionship} deleting={d.deleting} />
      <BanUserDialog isItalian={d.isItalian} open={d.showBanDialog} onOpenChange={d.setShowBanDialog} userToBan={d.userToBan} confirmBanUser={d.confirmBanUser} />
      <SelfBanDialog isItalian={d.isItalian} open={d.showSelfBanDialog} onOpenChange={d.setShowSelfBanDialog} confirmSelfBan={d.confirmSelfBan} />
      <EditClubDialog isItalian={d.isItalian} open={d.showEditClubDialog} onOpenChange={d.setShowEditClubDialog} editingClub={d.editingClub} editClubForm={d.editClubForm} setEditClubForm={d.setEditClubForm} savingClub={d.savingClub} handleSaveClub={d.handleSaveClub} handleUploadClubLogo={d.handleUploadClubLogo} editClubLogoRef={d.editClubLogoRef} />
      <SendCoinsDialog isItalian={d.isItalian} open={d.showSendCoinsDialog} onOpenChange={d.setShowSendCoinsDialog} coinsRecipient={d.coinsRecipient} coinsForm={d.coinsForm} setCoinsForm={d.setCoinsForm} sendingCoins={d.sendingCoins} handleSendCoins={d.handleSendCoins} />
      <RosterDialog isItalian={d.isItalian} open={d.showRosterDialog} onOpenChange={d.setShowRosterDialog} selectedClubRoster={d.selectedClubRoster} clubMembers={d.clubMembers} loadingRoster={d.loadingRoster} handleAssignRole={d.handleAssignRole} />
      <CreateClubDialog isItalian={d.isItalian} open={d.showCreateClubDialog} onOpenChange={d.setShowCreateClubDialog} createClubForm={d.createClubForm} setCreateClubForm={d.setCreateClubForm} creatingClub={d.creatingClub} availableCaptains={d.availableCaptains} championships={d.championships} handleCreateClub={d.handleCreateClub} />
      <ChangePasswordDialog isItalian={d.isItalian} open={d.showChangePasswordDialog} onOpenChange={d.setShowChangePasswordDialog} passwordTarget={d.passwordTarget} newPassword={d.newPassword} setNewPassword={d.setNewPassword} changingPassword={d.changingPassword} confirmChangePassword={d.confirmChangePassword} />
      <CreateUserDialog isItalian={d.isItalian} open={d.showCreateUserDialog} onOpenChange={d.setShowCreateUserDialog} createUserForm={d.createUserForm} setCreateUserForm={d.setCreateUserForm} creatingUser={d.creatingUser} handleCreateUser={d.handleCreateUser} />
      <AddMemberDialog isItalian={d.isItalian} open={d.showAddMemberDialog} onOpenChange={d.setShowAddMemberDialog} addMemberClub={d.addMemberClub} addMemberUserId={d.addMemberUserId} setAddMemberUserId={d.setAddMemberUserId} addingMember={d.addingMember} usersWithoutClub={d.usersWithoutClub} handleAddMemberToClub={d.handleAddMemberToClub} />
    </div>
  );
};

export default SuperAdmin;
